import { useState } from "react";
import { Link } from "react-router";
import { AuthShell } from "../components/AuthShell";
import { Trans, useTranslation } from "../i18n";
import { Alert, Button } from "../components/ui";
import { ApiError, api } from "../lib/api";

/**
 * The screen between registering and being able to sign in.
 *
 * It appears in two places — after registration, and after a sign-in refused for
 * an unconfirmed address — so it lives on its own rather than inside either. The
 * two arrive with different amounts of knowledge, which is what `emailSent` is
 * for: registration knows whether the platform managed to send anything, and the
 * login path does not.
 *
 * The resend answers the same way whatever happens, because the endpoint behind
 * it does: it takes an address, it is unauthenticated, and saying "no such
 * account" would make it an account-enumeration oracle. So the copy promises
 * only that *if* the address is waiting on confirmation, another message is on
 * its way — which is exactly what is true.
 */
export function CheckYourEmail({
  email,
  emailSent = true,
}: {
  email: string;
  /** False when the platform knows the message did not go out. */
  emailSent?: boolean;
}) {
  const { t } = useTranslation("auth");
  const [resent, setResent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function resend() {
    setError(null);
    setSending(true);
    try {
      await api<{ success: true }>("/v1/auth/verify-email/resend", {
        method: "POST",
        body: { email },
        anonymous: true,
      });
      setResent(true);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t("checkEmail.resendFailed"));
    } finally {
      setSending(false);
    }
  }

  return (
    <AuthShell
      title={t("checkEmail.title")}
      subtitle={t("checkEmail.subtitle", { email })}
      footer={
        <>
          {t("checkEmail.alreadyConfirmed")}{" "}
          <Link
            className="text-accent transition-colors duration-200 ease-smooth hover:text-accent-hover"
            to="/login"
          >
            {t("checkEmail.signIn")}
          </Link>
        </>
      }
    >
      <div className="flex flex-col items-center gap-5 text-center">
        {/*
          Said plainly when the platform knows it failed, rather than leaving
          somebody watching an inbox. This is the state where the resend button
          is the whole point of the screen, so it is not buried under an
          instruction to wait.
        */}
        {!emailSent && (
          <Alert>
            <Trans t={t} i18nKey="checkEmail.notSent" components={{ strong: <span className="font-medium" /> }} />
          </Alert>
        )}

        {error && <Alert>{error}</Alert>}
        {resent && <Alert tone="success">{t("checkEmail.sentAgain")}</Alert>}

        {/*
          One line. The link's lifetime is the one fact worth stating; "check
          spam" is the one instruction. A promotion code, if any, is confirmed
          on the page the link opens — no promise here.
        */}
        <p className="text-sm leading-6 text-fg-muted">
          {t("checkEmail.validFor", { hours: 24 })}
        </p>

        <Button variant="outline" loading={sending} onClick={() => void resend()}>
          {t("checkEmail.resend")}
        </Button>
      </div>
    </AuthShell>
  );
}
